import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  where,
  writeBatch,
} from 'firebase/firestore';

import { db } from '@/config/firebase';
import type {
  HealthPlanImportDocument,
  HealthPlanProvider,
  HealthPlanSourceFile,
  HealthPlanImportStatus,
  HealthPlanImportTotals,
  HealthPlanFamilyPreview,
  HealthPlanAuditItem,
} from './healthPlanTypes';

const HEALTH_PLAN_IMPORTS_COLLECTION = 'expenses_health_plan_imports';

function getCollectionRef() {
  return collection(db, HEALTH_PLAN_IMPORTS_COLLECTION);
}

function mapTotals(data: any): HealthPlanImportTotals {
  if (!data || typeof data !== 'object') return {};
  return {
    premiumHealth: typeof data.premiumHealth === 'number' ? data.premiumHealth : null,
    premiumDental: typeof data.premiumDental === 'number' ? data.premiumDental : null,
    copayTotal: typeof data.copayTotal === 'number' ? data.copayTotal : null,
    totalCost: typeof data.totalCost === 'number' ? data.totalCost : null,
    iofTax: typeof data.iofTax === 'number' ? data.iofTax : null,
    adjustmentsTotal: typeof data.adjustmentsTotal === 'number' ? data.adjustmentsTotal : null,
    billTotal: typeof data.billTotal === 'number' ? data.billTotal : null,
    activeLives: typeof data.activeLives === 'number' ? data.activeLives : null,
    financialCost: typeof data.financialCost === 'number' ? data.financialCost : null,
  };
}

function mapSourceFiles(data: any): HealthPlanSourceFile[] {
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => ({
      name: typeof item?.name === 'string' ? item.name : 'unknown',
      size: typeof item?.size === 'number' ? item.size : 0,
      contentType: typeof item?.contentType === 'string' ? item.contentType : null,
    }))
    .filter((file) => file.name);
}

function mapStatus(raw: any): HealthPlanImportStatus {
  const allowed: HealthPlanImportStatus[] = ['pending', 'processing', 'ready', 'error'];
  if (allowed.includes(raw)) return raw;
  return 'pending';
}

function mapDocument(document: any): HealthPlanImportDocument | null {
  const data = document.data();
  if (!data) return null;

  const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date();
  const provider = data.provider as HealthPlanProvider;

  if (!provider) return null;

  return {
    id: document.id,
    provider,
    billingPeriod: typeof data.billingPeriod === 'string' ? data.billingPeriod : null,
    status: mapStatus(data.status),
    totals: mapTotals(data.totals),
    sourceFiles: mapSourceFiles(data.sourceFiles),
    livesPreview: undefined,
    familiesPreview: Array.isArray((data as any)?.familiesPreview)
      ? ((data as any).familiesPreview as HealthPlanFamilyPreview[])
      : undefined,
    movementsPreview: Array.isArray(data.movementsPreview) ? (data.movementsPreview as any[]) : [],
    audit: Array.isArray(data.audit) ? (data.audit as HealthPlanAuditItem[]) : [],
    createdAt,
    createdByUid: typeof data.createdByUid === 'string' ? data.createdByUid : '',
    createdByEmail: typeof data.createdByEmail === 'string' ? data.createdByEmail : null,
    createdByName: typeof data.createdByName === 'string' ? data.createdByName : null,
    errorMessage: typeof data.errorMessage === 'string' ? data.errorMessage : null,
  };
}

export async function listHealthPlanImports(provider: HealthPlanProvider): Promise<HealthPlanImportDocument[]> {
  const importsQuery = query(getCollectionRef(), where('provider', '==', provider));
  const snap = await getDocs(importsQuery);
  const mapped = snap.docs.map(mapDocument).filter(Boolean) as HealthPlanImportDocument[];
  return mapped.sort((a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0));
}

export async function listHealthPlanImportsByCompetence(competence: string): Promise<HealthPlanImportDocument[]> {
  const importsQuery = query(getCollectionRef(), where('billingPeriod', '==', competence));
  const snap = await getDocs(importsQuery);
  const mapped = snap.docs.map(mapDocument).filter(Boolean) as HealthPlanImportDocument[];
  return mapped.sort((a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0));
}

export async function getHealthPlanImportById(importId: string): Promise<HealthPlanImportDocument | null> {
  const snapshot = await getDoc(doc(db, HEALTH_PLAN_IMPORTS_COLLECTION, importId));
  if (!snapshot.exists()) return null;
  return mapDocument(snapshot);
}

type CreateHealthPlanImportPayload = {
  billingPeriod: string | null;
  totals?: HealthPlanImportTotals;
  sourceFiles: HealthPlanSourceFile[];
  status?: HealthPlanImportStatus;
  familiesPreview?: HealthPlanFamilyPreview[];
  movementsPreview?: any[];
  audit?: HealthPlanAuditItem[];
};

export async function createHealthPlanImport(
  provider: HealthPlanProvider,
  payload: CreateHealthPlanImportPayload,
  user: { uid: string; email?: string | null; displayName?: string | null },
): Promise<HealthPlanImportDocument> {
  const docRef = await addDoc(getCollectionRef(), {
    provider,
    billingPeriod: payload.billingPeriod ?? null,
    status: payload.status ?? 'pending',
    totals: payload.totals ?? {},
    sourceFiles: payload.sourceFiles ?? [],
    familiesPreview: payload.familiesPreview ?? [],
    movementsPreview: payload.movementsPreview ?? [],
    audit: payload.audit ?? [],
    createdAt: serverTimestamp(),
    createdByUid: user.uid,
    createdByEmail: user.email ?? null,
    createdByName: user.displayName ?? null,
  });

  return {
    id: docRef.id,
    provider,
    billingPeriod: payload.billingPeriod ?? null,
    status: payload.status ?? 'pending',
    totals: payload.totals ?? {},
    sourceFiles: payload.sourceFiles ?? [],
    familiesPreview: payload.familiesPreview ?? [],
    movementsPreview: payload.movementsPreview ?? [],
    audit: payload.audit ?? [],
    createdAt: new Date(),
    createdByUid: user.uid,
    createdByEmail: user.email ?? null,
    createdByName: user.displayName ?? null,
    errorMessage: null,
  };
}

export async function deleteHealthPlanImportById(importId: string): Promise<void> {
  await deleteDoc(doc(db, HEALTH_PLAN_IMPORTS_COLLECTION, importId));
}

type HealthPlanIdentifierAssignment = {
  collaboratorId: string;
  userCode?: string | null;
  familyIdentifier?: string | null;
  certificateCode?: string | null;
};

export async function saveHealthPlanIdentifierAssignments(
  provider: HealthPlanProvider,
  assignments: HealthPlanIdentifierAssignment[],
): Promise<void> {
  const normalized = assignments
    .map((assignment) => {
      const source = assignment.userCode ?? assignment.familyIdentifier ?? assignment.certificateCode ?? null;
      if (!source) return null;
      const digits = source.replace(/\D/g, '');
      if (!digits) return null;
      return { collaboratorId: assignment.collaboratorId, identifier: digits };
    })
    .filter(Boolean) as { collaboratorId: string; identifier: string }[];

  if (!normalized.length) return;

  const batch = writeBatch(db);
  normalized.forEach(({ collaboratorId, identifier }) => {
    const ref = doc(db, 'collaborators_private', collaboratorId);
    batch.set(ref, { healthPlanIdentifiers: { [provider]: identifier } }, { merge: true });
  });

  await batch.commit();
}
